import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, getRedirectResult, getIdToken, applyActionCode, reload, verifyPasswordResetCode, confirmPasswordReset as confirmPasswordResetFirebase, reauthenticateWithCredential, updateEmail, updatePassword, EmailAuthProvider, deleteUser, signInWithEmailLink as signInWithEmailLinkFirebase, fetchSignInMethodsForEmail as fetchSignInMethodsForEmailFirebase, unlink, signInAnonymously as signInAnonymouslyFirebase, updateProfile as updateProfileFirebase } from "firebase/auth";
import { redirect, useLocation, Navigate } from 'react-router'
import * as Sentry from "@sentry/react";
import { publicAction } from "@/data/loaders";

const DEVELOPERS = [
  "OOc8U6esvoXJn65qjcpJrqSQ3ln2", // terren gmail
  "92IYCQlM5GfSAHRExW9fAj8CRjV2", // terren sapient
  "y9s8V9edbwREQXgetKOw60HhmYW2", // aram sapient
  "IwCSMCVkHvgFpUbQwCvtwDTDQkh1", // superadmin
]
const SUPERADMIN = "IwCSMCVkHvgFpUbQwCvtwDTDQkh1";
export const TERMS_VERSION = "2025-04-10";

const UserContext = createContext(null);

const firebaseConfig = {
  apiKey: "AIzaSyBxJdD7P3bgiQDfvaJRZLwyrQAuBsA1Vb0",
  authDomain: "app.sapientclinician.com",
  databaseURL: "https://sapientclinician-default-rtdb.firebaseio.com",
  projectId: "sapientclinician",
  storageBucket: "sapientclinician.appspot.com",
  messagingSenderId: "811040050451",
  appId: "1:811040050451:web:c8e3e7a0dd7804cadb1ee6",
  measurementId: "G-18EJQCX56J"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

export const signOut = function(withoutContinueUrl = false) {
  if (withoutContinueUrl) {
    localStorage.setItem("last_signed_out", Date.now());
  }
  Sentry.setUser(null);
  return auth.signOut();
}

export const AuthenticationRoot = function({ children }) {
  const [user, setUser] = useState(undefined);
  const [userdata, setUserdata] = useState(undefined);
  const [developerModeEnabled, setDeveloperModeEnabled] = useState(localStorage.getItem("sapientDeveloperModeEnabled") == "true");
  const [privacyMode, setPrivacyModeState] = useState(localStorage.getItem("privacyMode") == "true");

  const setPrivacyMode = useCallback(function(value) {
    setPrivacyModeState(value);
    localStorage.setItem("privacyMode", value ? "true" : "false");
  }, []);

  useEffect(() => {
    onUserChange((user) => {
      setUser(user);
      // if (!user || !user?.email?.endsWith("@sapientclinician.com")) {
      //   setPrivacyMode(false);
      // }
      // if (!user || !DEVELOPERS.includes(user?.uid)) {
      //   setDeveloperModeEnabled(false);
      // }
      // This caused infinite re-renders
    });
    onUserdataChange((userdata) => {
      setUserdata(userdata);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem("sapientDeveloperModeEnabled", developerModeEnabled ? "true" : "false");
  }, [developerModeEnabled]);
  
  useEffect(() => {
    function handleKeyDown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
        if (user && DEVELOPERS.includes(user.uid)) {
          event.preventDefault();
          setDeveloperModeEnabled(!developerModeEnabled);
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [developerModeEnabled, user]);

  return (
    <>
      <UserContext.Provider value={{ user, userdata, developerModeEnabled, privacyMode, setPrivacyMode }}>
        {children}
      </UserContext.Provider>
    </>
  )
}

let user = undefined;
let await_user = []; // [callback, persistent]
let userdata = undefined;
let await_userdata = []; // [callback, persistent, error_callback]
let last_login_error;
let await_last_login_error = [];

function setUser(u) {
  user = u;
  await_user.forEach(p => p[0](user));
  await_user = await_user.filter(p => p[1]);
}

function setUserdata(ud) {
  userdata = ud;
  userdata_is_stale = false;
  await_userdata.forEach(p => p[0](userdata));
  await_userdata = await_userdata.filter(p => p[1]);
}

function setUserdataError(error) {
  await_userdata.forEach(p => p[2] ? p[2](error) : null);
  await_userdata = await_userdata.filter(p => p[1]);
}

function onUserChange(user_changed) {
  if (user !== undefined) {
    user_changed(user);
  }
  await_user.push([user_changed,true]);
}

function onUserdataChange(userdata_changed) {
  if (userdata !== undefined) {
    userdata_changed(userdata);
  }
  await_userdata.push([userdata_changed,true]);
}

function awaitUser(callback) {
  if (user !== undefined) {
    callback();
  } else {
    await_user.push([callback,false])
  }
}

function awaitFreshUserdata(callback, error_callback) {
  if (userdata !== undefined && !userdata_is_stale) {
    callback();
  } else {
    await_userdata.push([callback,false,error_callback])
  }
}

function setLastLoginError(value) {
  last_login_error = value;
  await_last_login_error.forEach(p => p());
  await_last_login_error = [];
}

function awaitLastLoginError(callback) {
  if (last_login_error !== undefined) {
    callback();
  } else {
    await_last_login_error.push(callback)
  }
}

export function isCompleteUserdata(ud) {
  return ud && ud.terms_accepted == TERMS_VERSION && ud.name && ud.role && (ud.organization_id || ud.approved);
}

onAuthStateChanged(auth, (u) => {
  user = u;

  setUser(user);

  if (user) {
    last_login_error = false;
    Sentry.setUser({
      id: user.uid,
      email: user.email
    });
  } else {
    setUserdata(undefined);
    refreshUserdata();
    Sentry.setUser(null);
  }
});

export function refreshUserdata() {
  userdata_is_stale = true;
  loadUserdata().then(() => {}).catch(() => {});
}

let last_userdata_load_time = 0;
let userdata_is_stale = false;
function loadUserdata() {
  return new Promise(function (resolve, reject) {
    if (userdata && !userdata_is_stale) {
      resolve();
      return; 
    }

    let load_time = Date.now();
    last_userdata_load_time = load_time;

    const providerToLink = JSON.parse(localStorage.getItem("providerToLink") || "null");

    if ((auth.currentUser.emailVerified || auth.currentUser.isAnonymous) && !(providerToLink && providerToLink.email == auth.currentUser.email && !user.providerData.find(a => a.providerId == providerToLink.provider))) {
      if (user.providerData.length > 1 && user.providerData.find(a => a.providerId == "password")) {
        unlink(auth.currentUser, "password").then(function() {
          console.log("Unlinked password provider");
        }).catch(function(error) {
          console.error(error);
        });
      }

      publicAction("getProfile", {}, { dontWait: true }).then(function({ data }) {
        if (last_userdata_load_time == load_time) {
          let new_userdata = data.value

          // new_userdata.root_organizations = {};

          // new_userdata.organizations.forEach(org => {
          //   new_userdata.root_organizations[org.root_data._id] = org.root_data;
          // })

          setUserdata(new_userdata);
          resolve();
        } else {
          reject(new Error("Userdata load cancelled"));
        }
      }).catch(function(error) {
        if (last_userdata_load_time == load_time) {
          setUserdataError(error);
        }
        reject(error);
      })

    } else {
      setUserdata({});
      resolve();
    }
  });
}

export function loadSignedInRoute({request}) {
  return new Promise(function (resolve, reject) {
    awaitUser(function() {
      if (user) {
        loadUserdata().then(function() {
          if (isCompleteUserdata(userdata)) {
            resolve({});
          } else {
            noUserOrIncompleteUserdata();
          }
        }).catch(reject);
      } else {
        noUserOrIncompleteUserdata();
      }
      
      function noUserOrIncompleteUserdata() {
        let pathname = request ? (new URL(request.url)).pathname : "/";
        resolve(redirect("/login" + ((pathname !== "/" && pathname !== "/scenarios" && pathname !== "/login") ? "?continue="+pathname : ""), {replace: true}));
      }
    })
  })
}

export function loadRootRoute({request}) {
  return new Promise(function (resolve, reject) {
    awaitUser(function() {
      if (user) {
        awaitFreshUserdata(function() {
          resolve(redirect(isCompleteUserdata(userdata) ? (userdata.is_educator ? "/scenarios" : "/courses") : "/login"));
        }, () => {reject(new Error("Failed to get userdata"))})
      } else {
        resolve(redirect("/login", {replace: true}));
      }
    })
  })
}

getRedirectResult(auth).then((result) => {
  setLastLoginError(false);
}).catch((error) => {
  console.error(error);
  setLastLoginError({error: error, email: error.customData.email});
});

export function SignedInRoute({children}) {
  const { user } = useUser();
  const location = useLocation();
  const last_signed_out = Number(localStorage.getItem("last_signed_out"));
  const signed_out_recently = last_signed_out && last_signed_out > Date.now() - 500;

  // if (user === undefined) {
  //   return children;
  // }
  return user ? children : <Navigate to={signed_out_recently ? "/login" : "/login?continue="+location.pathname} replace={true} />
}

export function SignedOutRoute({children}) {
  return children;

  // const { user } = useUser();
  // const location = useLocation();
  // const navigation = useNavigation();

  // let params = new URLSearchParams(location.search);

  // if (user === undefined) {
  //   return children;
  // }
  // return (user && navigation.state !== "loading") ? <Navigate to={params.has("continue") ? params.get("continue") : "/"} replace /> : (children)
}

export function loadSignedOutRoute({request}) {
  return new Promise(function (resolve, reject) {
    awaitUser(function() {
      awaitLastLoginError(function() {
        if (user) {
          loadUserdata().then(function() {
            if (isCompleteUserdata(userdata)) {
              let params = new URLSearchParams((new URL(request.url)).search);
              resolve(redirect(params.has("continue") ? params.get("continue") : "/"), {replace: true});
            } else {
              resolve({})
            }
          }).catch(reject);
        } else {
          resolve({last_login_error: last_login_error})
        }
      })
    })
  })
}

export function getToken({ dontWait = false, allowIncompleteUserdata = false, educatorOnly = false } = {}) {
  return new Promise(function (resolve, reject) {
    awaitUser(function() {
      if (user) {
        if (dontWait) {
          getIdToken(user).then(function (token) {
            resolve(token);
          }).catch(function (error) {
            reject(error);
          })
        } else {
          awaitFreshUserdata(function() {
            if (userdata && (isCompleteUserdata(userdata) || allowIncompleteUserdata)) {
              if (!educatorOnly || userdata.is_educator) {
                getIdToken(user).then(function (token) {
                  resolve(token);
                }).catch(function (error) {
                  reject(error);
                })
              } else {
                reject(new Error("Educator only"))
              }
            } else {
              reject(new Error("Incomplete userdata"))
            }
          }, () => {reject(new Error("Failed to get userdata"))})
        }
      } else {
        reject(new Error("No user"))
      }
    })
  })
}

export function getUserInLoader() {
  return new Promise(function(resolve, reject) {
    awaitUser(function() {
      if (user) {
        awaitFreshUserdata(function() {
          if (userdata && isCompleteUserdata(userdata)) {
            resolve({ user, userdata })
          } else {
            reject(new Error("Incomplete userdata"))
          }
        }, () => {reject(new Error("Failed to get userdata"))})
      } else {
        reject(new Error("No user"))
      }
    });
  });
}

export function useUser() {
  const { user, userdata, developerModeEnabled } = useContext(UserContext);
  return { user, uid: user?.uid, userdata, isCompleteUserdata: user && isCompleteUserdata(userdata), developerModeEnabled };
}

export function useIsDeveloper() {
  const { user, developerModeEnabled } = useUser();
  return user && DEVELOPERS.includes(user.uid) && developerModeEnabled;
}

export function usePrivacyMode() {
  const { privacyMode } = useContext(UserContext);
  return privacyMode;
}

export function useSetPrivacyMode() {
  const { privacyMode, setPrivacyMode } = useContext(UserContext);
  return { privacyMode, setPrivacyMode };
}

export function useIsSuperAdmin() {
  const { user } = useUser();
  return user && user.uid == SUPERADMIN;
}

export const sendEmailVerification = function() {
  return new Promise(function(resolve, reject) {
    publicAction("verifyEmail", { email: auth.currentUser.email }, { unauthenticated: true }).then(function({ data }) {
      if (data.message == "Too many attempts") {
        reject(new Error("Too many attempts"));
      } else {
        resolve();
      }
    }).catch(reject);
  })
}

export const verifyEmailOobCode = function(oobCode) {
  return applyActionCode(auth, oobCode)
}

export const useWaitForUserToHaveEmailVerified = function({ enabled, callback }) {
  useEffect(() => {
    if (!enabled || !auth.currentUser) return;
    
    let timeoutId;
    let totalTime = 0;
    let isVerified = false;
    
    const checkEmailVerification = (manual = false) => {
      if (isVerified) return;
      
      reload(auth.currentUser).then(() => {
        if (auth.currentUser.emailVerified && !isVerified) {
          isVerified = true;
          if (typeof callback === 'function') {
            callback();
          }
          return;
        }
        if (manual) {
          return;
        }
        
        totalTime += 1;
        
        // Stop after 10 minutes (600 seconds)
        if (totalTime > 600) {
          return;
        }
        
        // Check every 10 seconds for first 3 minutes, then every 30 seconds
        const interval = totalTime <= 180 ? 10000 : 30000;
        timeoutId = setTimeout(checkEmailVerification, interval);
      }).catch(error => {
        console.error("Error reloading user:", error);
      });
    };
    
    // Initial check
    timeoutId = setTimeout(checkEmailVerification, 10000);
    
    // Check when tab is focused
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isVerified) {
        checkEmailVerification(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, callback]);
}

export const sendPasswordResetEmail = function(email) {
  return new Promise(function(resolve, reject) {
    publicAction("passwordReset", { email: email }, { unauthenticated: true }).then(function({ data }) {
      resolve();
    }).catch(reject);
  })
}

export const verifyPasswordResetOobCode = function(oobCode) {
  return verifyPasswordResetCode(auth, oobCode);
}

export const confirmPasswordReset = function(oobCode, password) {
  return confirmPasswordResetFirebase(auth, oobCode, password);
}

// export const changeEmail = function(newEmail, password) {
//   let credential = EmailAuthProvider.credential(auth.currentUser.email, password);
//   return reauthenticateWithCredential(auth.currentUser, credential).then(function() {
//     return updateEmail(auth.currentUser, newEmail).then(function() {
//       return reload(auth.currentUser);
//     })
//   })
// }

export const sendEmailChangeEmail = function(newEmail, password) {
  let credential = EmailAuthProvider.credential(auth.currentUser.email, password);
  return reauthenticateWithCredential(auth.currentUser, credential).then(function() {
    return new Promise(function(resolve, reject) {
      publicAction("changeEmail", { new_email: newEmail }, { allowIncompleteUserdata: true }).then(function({ data }) {
        if (data.message == "Too many attempts") {
          reject(new Error("Too many attempts"));
        } else {
          resolve();
        }
      }).catch(reject);
    })
  })
}

export const changePassword = function(password, new_password) {
  let credential = EmailAuthProvider.credential(auth.currentUser.email, password);
  return reauthenticateWithCredential(auth.currentUser, credential).then(function() {
    return updatePassword(auth.currentUser, new_password)
  })
}

export const revokeSession = function() {
  return publicAction("revokeSession", {}, { allowIncompleteUserdata: true });
}

export const deleteAccount = function(provider, password) {
  if (provider == "password") {
    let credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    return reauthenticateWithCredential(auth.currentUser, credential).then(function() {
      return deleteUser(auth.currentUser)
    })
  } else {
    return deleteUser(auth.currentUser);
  }
}

export const signInWithEmailCode = function(email) {
  return new Promise(function(resolve, reject) {
    publicAction("signInWithEmailCode", { email: email }, { unauthenticated: true }).then(function({ data }) {
      resolve({providerToLink: data.providerToLink});
    }).catch(reject);
  })
}

export const verifyEmailCode = function(email, code) {
  return new Promise(function(resolve, reject) {
    publicAction("verifyEmailCode", { email: email, code: code }, { unauthenticated: true }).then(function({ data }) {
      resolve(data);
    }).catch(reject);
  })
}

export const signInWithEmailLink = function(email, oobCode) {
  return signInWithEmailLinkFirebase(auth, email, "https://app.sapientclinician.com/__/auth/action?apiKey=AIzaSyBxJdD7P3bgiQDfvaJRZLwyrQAuBsA1Vb0&mode=signIn&oobCode="+oobCode+"&continueUrl=https://app.sapientclinician.com&lang=en");
}

export const fetchSignInMethodsForEmail = function(email) {
  return fetchSignInMethodsForEmailFirebase(auth, email);
}

export function loadStudy({ request, params }) {
  return new Promise(function(resolve, reject) {

    publicAction("loadStudy", { join_code: params.join_code }, { unauthenticated: true }).then(function({ data }) {
      if (data.value) {
        awaitUser(function() {
          const last_anonymous_sign_in = JSON.parse(localStorage.getItem("last_anonymous_sign_in") || "null");
          if (user && last_anonymous_sign_in && last_anonymous_sign_in.source_id == "study-"+params.join_code && last_anonymous_sign_in.uid == user.uid) {
            resolve(redirect("/join/"+params.join_code, {replace: true}));
          } else {
            resolve({data: data});
          }
        })
      } else {
        reject({text: "This study has ended or the link is invalid.", path: "/login", status: 404 });
      }
    }).catch(reject);

  })
}

export function loadDemo({ request, params }) {
  return new Promise(function(resolve, reject) {

    const join_code = params.join_code;
    const assessment_id = params.assessment_id;

    awaitUser(function() {
      const last_anonymous_sign_in = JSON.parse(localStorage.getItem("last_anonymous_sign_in") || "null");
      if (user && last_anonymous_sign_in && last_anonymous_sign_in.source_id == "demo" && last_anonymous_sign_in.uid == user.uid) {
        resolve(redirect("/join/"+join_code+"/"+assessment_id, {replace: true}));
      } else {
        resolve({});
      }
    })

  })
}

export const signInAnonymously = function({ name, source_id }) {
  return new Promise(function(resolve, reject) {
    signInAnonymouslyFirebase(auth).then(function(result) {
      if (source_id) {
        localStorage.setItem("last_anonymous_sign_in", JSON.stringify({ source_id, uid: result.user.uid }));
      }
      updateProfileFirebase(auth.currentUser, {
        displayName: name
      }).then(() => {
        resolve();
      }).catch((error) => {
        signOut();
        reject(error);
      });
    }).catch(reject);
  });
}